import { QueryClient } from '@tanstack/react-query';
import { normalize } from 'normalizr';
import React, { useEffect, useMemo, useState } from 'react';
import { LoaderFunctionArgs, data, useParams } from 'react-router';

import Loading from '~/components/Loading';

import { ADD_ENTITIES } from '~/modules/entity';
import { galleryImageSchema } from '~/modules/gallery';
import { galleryImageQuery, useGalleryImage } from '~/modules/gallery/queries';

import { useAppDispatch } from '~/hooks/redux';

import PasswordPrompt from '../_components/PasswordPrompt';
import View from './_components/View';

function GalleryImage() {
  const { imageId, password: urlPassword } = useParams();
  const [password, setPassword] = useState(urlPassword ?? null);
  const dispatch = useAppDispatch();

  const { isLoading, data } = useGalleryImage(imageId, password);
  const image = data?.image;
  const isProtected = useMemo(() => data?.isProtected, [data]);

  useEffect(() => {
    if (image) {
      const normalized = normalize(image, galleryImageSchema);
      console.log('Normalized data:', normalized);
      dispatch({
        type: ADD_ENTITIES,
        payload: normalized.entities,
      });
    }
  }, [image, dispatch]);

  // If protected and missing correct password, show password prompt
  if (isProtected && !password) {
    return (
      <PasswordPrompt
        handleSubmit={(values) => {
          setPassword(values.password);
        }}
      />
    );
  }

  if (isLoading || !image) {
    return <Loading message="Loading image..." />;
  }

  return <View image={image} />;
}

export const loader =
  (queryClient: QueryClient) =>
  async ({ params: { imageId, password } }: LoaderFunctionArgs) => {
    if (!imageId) throw data('Image ID is required', { status: 400 });
    try {
      await queryClient.fetchQuery(galleryImageQuery(imageId, password));
    } catch (error: Response | any) {
      if (error.status === 404) {
        throw data('Image not found', { status: 404 });
      }
      return {};
    }
    return {};
  };

export default GalleryImage;
